import clsx from "clsx";

import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "../Button";
import Spinner from "../Spinner";
import { t } from "../../i18n";
import { alertTriangleIcon } from "../icons";

import {
  formatMermaidParseErrorMessage,
  getMermaidSyntaxErrorGuidance,
  isMermaidCaretLine,
} from "./utils/mermaidError";
import {
  PREVIEW_IDENTITY_TRANSFORM,
  isPreviewTransformed,
  panPreview,
  wheelZoomFactor,
  zoomPreviewAt,
} from "./utils/previewTransform";

import type { PreviewTransform } from "./utils/previewTransform";

interface TTDDialogOutputProps {
  error: Error | null;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  loaded: boolean;
  hideErrorDetails?: boolean;
  sourceText?: string;
  autoFixAvailable?: boolean;
  onApplyAutoFix?: () => void;
}

export const TTDDialogOutput = ({
  error,
  canvasRef,
  loaded,
  hideErrorDetails,
  sourceText,
  autoFixAvailable,
  onApplyAutoFix,
}: TTDDialogOutputProps) => {
  const errorMessage = error
    ? hideErrorDetails
      ? t("chat.errors.mermaidParseError")
      : formatMermaidParseErrorMessage(error.message)
    : null;
  const syntaxGuidance =
    error && !hideErrorDetails
      ? getMermaidSyntaxErrorGuidance(error.message, sourceText)
      : null;
  const showAutoFixButton =
    !!autoFixAvailable && !!onApplyAutoFix && !hideErrorDetails;

  const errorMessageLines = errorMessage?.split(/\r?\n/) ?? [];

  const viewportRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState<PreviewTransform>(
    PREVIEW_IDENTITY_TRANSFORM,
  );
  // drag origin, set on pointerdown and cleared on pointerup
  const panFromRef = useRef<{ x: number; y: number } | null>(null);

  // React registers wheel handlers passively, so preventDefault() there is a
  // no-op and the dialog would scroll behind the zoom — bind it natively.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      setTransform((current) =>
        zoomPreviewAt(
          current,
          wheelZoomFactor(event.deltaY),
          event.clientX - rect.left,
          event.clientY - rect.top,
        ),
      );
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
    // `loaded` gates whether the viewport is mounted at all — without it the
    // effect runs once against a null ref and never binds
  }, [loaded]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // left button / touch only — right-click keeps the browser menu
      if (event.button !== 0) {
        return;
      }
      panFromRef.current = { x: event.clientX, y: event.clientY };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const panFrom = panFromRef.current;
      if (!panFrom) {
        return;
      }
      panFromRef.current = { x: event.clientX, y: event.clientY };
      setTransform((current) =>
        panPreview(
          current,
          event.clientX - panFrom.x,
          event.clientY - panFrom.y,
        ),
      );
    },
    [],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      panFromRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [],
  );

  const resetTransform = useCallback(
    () => setTransform(PREVIEW_IDENTITY_TRANSFORM),
    [],
  );

  return (
    <div
      className={`ttd-dialog-output-wrapper ${
        error ? "ttd-dialog-output-wrapper--error" : ""
      }`}
    >
      {error && (
        <div
          key="error"
          data-testid="ttd-dialog-output-error"
          className="ttd-dialog-output-error"
        >
          <div className="ttd-dialog-output-error-content">
            <div className="ttd-dialog-output-error-icon">
              {alertTriangleIcon}
            </div>
            {syntaxGuidance && (
              <div className="ttd-dialog-output-error-summary">
                <div className="ttd-dialog-output-error-summary__headline">
                  {syntaxGuidance.summary}
                </div>
                <div className="ttd-dialog-output-error-summary__label">
                  Likely causes:
                </div>
                <ul className="ttd-dialog-output-error-summary__causes">
                  {syntaxGuidance.likelyCauses.map((cause) => (
                    <li key={cause}>{cause}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="ttd-dialog-output-error-message">
              {errorMessageLines.map((line, index) => (
                <span
                  key={`error-line-${index}`}
                  className={
                    isMermaidCaretLine(line)
                      ? "ttd-dialog-output-error-message__caret"
                      : undefined
                  }
                >
                  {line}
                  {index < errorMessageLines.length - 1 ? "\n" : ""}
                </span>
              ))}
            </div>
            {!hideErrorDetails && (
              <div className="ttd-dialog-output-error-autofix-slot">
                {showAutoFixButton ? (
                  <Button
                    className="ttd-dialog-panel-button ttd-dialog-output-error-autofix"
                    onSelect={onApplyAutoFix}
                  >
                    {t("mermaid.autoFixAvailable")}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
      {loaded ? (
        <div
          key="canvas"
          ref={viewportRef}
          className={clsx("ttd-dialog-output-canvas-container", {
            invisible: !!error,
          })}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={resetTransform}
        >
          <div
            ref={canvasRef}
            className="ttd-dialog-output-canvas-content"
            style={{
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            }}
          />
          {isPreviewTransformed(transform) && (
            <button
              type="button"
              className="ttd-dialog-output-reset-zoom"
              // the container's own dblclick also resets; this is the
              // discoverable version
              onPointerDown={(event) => event.stopPropagation()}
              onClick={resetTransform}
            >
              {Math.round(transform.scale * 100)}% · reset
            </button>
          )}
        </div>
      ) : (
        <Spinner size="2rem" />
      )}
    </div>
  );
};
